import type {
  OrderInfo,
  Shop,
  AddressSuggestion,
  GeoPoint
} from '@@partner:types:app'
import type { SlotEvent } from '../slot/SlotsTable'

import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { mergeLeft, mergeLeftOnly } from 'merge-left-utils'
import { useFetchBrandShops } from '@@partner:graphql/query/order/brandShops.gql'
import { useUpdateOrderInfo } from '@@partner:graphql/mutation/order/updateOrderInfo.gql'
import { useCurrentCart } from '../cart/CartContext'
import { useValidatableForm } from '@@partner:helpers/hooks/rsuite'
import {
  validateAddressSuggestion,
  normalizeAddressSuggestion
} from '@@partner:helpers/address'
import { formatPrice } from '@@partner:helpers/util'
import { makeGraphQLErrorNotification } from '@@partner:helpers/notification'

import {
  makeDefaultState,
  OrderFormState,
  mapStateToApiVariable
} from './OrderFormState'

import {
  Form,
  RadioGroup,
  Radio,
  FlexboxGrid,
  Divider,
  Button,
  ButtonGroup,
  Grid,
  Row,
  Col,
  InputNumber
} from 'rsuite'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'
import CustomerAddress from '../form/CustomerAddress'
import OrderCost from '../common/OrderCost'
import OrderTabbedCart from '../cart/OrderTabbedCart'
import SlotsTable from '../slot/SlotsTable'
import FormElementErrorDisplay from '@components/common/form/FormElementErrorDisplay'
import FormCheckbox from '@components/common/form/FormCheckbox'
import FormTextareaControl from '@components/common/form/FormTextareaControl'

import ValidationModel from './validation/OrderFormValidationModel'

import { PAYMENT_METHODS } from '@@partner:config/payment-types'
import { SHIPPING_TYPES } from '@@partner:config/shipping-types'

import './OrderForm.scss'
import './OrderFormAddress.scss'

export interface OrderFormProps {
  orderCanBePlaced: boolean
  order: OrderInfo
  onFinish: (wantToPlaceOrder: boolean) => void
  onShopChange?: () => void
}

const FORM_PART_SHOP_PICKER = 'shop:picker'
const FORM_PART_ADDRESS_PICKER = 'customer:address'

enum FORM_SUBMIT_TYPES {
  SUBMIT_TO_SAVE,
  SUBMIT_TO_PUBLISH
}

export default function OrderForm ({
  orderCanBePlaced = false,
  order,
  onShopChange,
  onFinish
}: OrderFormProps) {
  const submitType = useRef<FORM_SUBMIT_TYPES>(FORM_SUBMIT_TYPES.SUBMIT_TO_SAVE)

  const { formState, setFormState, setFormRef } = useValidatableForm(() =>
    makeDefaultState(order)
  )

  const [ cartIsValid, setCartIsValid ] = useState(false)
  const [ currentlyUpdating, setCurrentlyUpdating ] = useState<
    string | undefined
  >()

  const { setShopID } = useCurrentCart()
  const { data, loading: loadingShops } = useFetchBrandShops()

  const [ updateOrderInfo, { loading: loadingUpdate, error } ] =
    useUpdateOrderInfo()

  const shippingType = useMemo(
    () => SHIPPING_TYPES?.[formState.shippingSide] ?? SHIPPING_TYPES.pickup,
    [ formState ]
  )

  const cartErrorLabel = useMemo(
    () =>
      order.shop?.minOrderCost ?? 0 > 0
        ? `Добавьте товары на сумму не менее ${formatPrice(
          order.shop?.minOrderCost
        )}`
        : 'Добавьте товары в корзину',
    [ order.shop?.minOrderCost ]
  )

  // handle pickup shipping option
  const handleShippingChange = useCallback(
    (shippingType) => {
      const shipping = SHIPPING_TYPES?.[shippingType]

      // set geopoint from shop info
      if (!shipping.needAddress) {
        setFormState((state) =>
          mergeLeftOnly([ 'address', 'customerGeoPoint' ], state, {
            customerGeoPoint: order.shop?.shopGeoPoint,
            address: order.shop?.address
          })
        )
      }
    },
    [ order.shop?.address, order.shop?.shopGeoPoint, setFormState ]
  )

  const handleShopPick = useCallback(
    (shop: Shop) => {
      setCurrentlyUpdating(FORM_PART_SHOP_PICKER)

      updateOrderInfo({
        variables: {
          order_id: order.id,
          data: { shop_id: shop.id }
        }
      })
        .then(({ data }) => {
          onShopChange?.()
          setShopID(shop.id)
          setFormState((state) =>
            mergeLeftOnly([ 'shop_id' ], state, data?.updated ?? {})
          )
        })
        .catch(makeGraphQLErrorNotification('Не удалось обновить заказ'))
    },
    [ onShopChange, order.id, setShopID, setFormState, updateOrderInfo ]
  )

  const handleAddressChosen = (suggestion: AddressSuggestion) => {
    if (validateAddressSuggestion(suggestion)) {
      if (suggestion.data.geo_lat && suggestion.data.geo_lon) {
        const customerGeoPoint: GeoPoint = {
          latitude: parseFloat(suggestion.data.geo_lat),
          longitude: parseFloat(suggestion.data.geo_lon)
        }

        setCurrentlyUpdating(FORM_PART_ADDRESS_PICKER)

        const updateState = {
          customerGeoPoint,
          ...normalizeAddressSuggestion(suggestion)
        }

        setFormState((state) => mergeLeft(state, updateState))

        updateOrderInfo({
          variables: {
            order_id: order.id,
            data: {
              customerGeoPoint
            }
          }
        })
      }
    }
  }

  const handleFormChange = useCallback(
    (updatedState: any & OrderFormState) => {
      setFormState((state: any) => mergeLeft(state, updatedState))
    },
    [ setFormState ]
  )

  const handleSubmit = useCallback(
    (formIsValid: boolean) => {
      if (formIsValid && cartIsValid) {
        const isUserWantPlaceOrder =
          submitType.current === FORM_SUBMIT_TYPES.SUBMIT_TO_PUBLISH

        updateOrderInfo({
          variables: {
            order_id: order.id,
            data: mapStateToApiVariable(formState)
          }
        }).then(() => {
          onFinish?.(isUserWantPlaceOrder)
        })
      }
    },
    [ cartIsValid, onFinish, order.id, formState, updateOrderInfo ]
  )

  const handleSlotSelect = useCallback(
    (slot: SlotEvent) => {
      setFormState((state) => mergeLeftOnly([ 'slot_id' ], state, slot))
    },
    [ setFormState ]
  )

  // run handlers when shippingSide has been changed
  useEffect(() => {
    if (order.shippingSide) {
      handleShippingChange(order.shippingSide)
    }
  }, [ handleShippingChange, order.shippingSide ])

  return (
    <Form
      model={ValidationModel}
      formValue={formState}
      onChange={handleFormChange}
      onSubmit={handleSubmit}
      ref={setFormRef}
    >
      <GraphQLCommonErrorDisplay
        title="Ошибка сохранения данных"
        error={error}
      />

      <FlexboxGrid className="OrderForm__Block my-2">
        <FlexboxGrid.Item colspan={24}>
          <h3 className="u-form-heading">Получатель</h3>
        </FlexboxGrid.Item>
        <FlexboxGrid.Item colspan={8}>
          <Form.Group>
            <Form.ControlLabel>Телефон получателя</Form.ControlLabel>
            <Form.Control
              name="recipientInfo.phone"
              placeholder="Телефон получателя"
            />
          </Form.Group>
        </FlexboxGrid.Item>
        <FlexboxGrid.Item colspan={8}>
          <Form.Group>
            <Form.ControlLabel>Фамилия получателя</Form.ControlLabel>
            <Form.Control
              name="recipientInfo.firstName"
              placeholder="Фамилия получателя"
            />
          </Form.Group>
        </FlexboxGrid.Item>
        <FlexboxGrid.Item colspan={8}>
          <Form.Group>
            <Form.ControlLabel>Имя получателя</Form.ControlLabel>
            <Form.Control
              name="recipientInfo.lastName"
              placeholder="Имя получателя"
            />
          </Form.Group>
        </FlexboxGrid.Item>
      </FlexboxGrid>

      <FlexboxGrid className="my-2">
        <FlexboxGrid.Item colspan={24}>
          <h3 className="u-form-heading">Доставка/оплата</h3>
        </FlexboxGrid.Item>

        <FlexboxGrid.Item colspan={6}>
          <Form.ControlLabel>Доставка</Form.ControlLabel>
          <Form.Control
            accepter={RadioGroup}
            onChange={handleShippingChange}
            name="shippingSide"
          >
            {Object.entries(SHIPPING_TYPES).map(([ key, settings ]) => (
              <Radio key={key} value={key}>
                {settings.label}
              </Radio>
            ))}
          </Form.Control>
        </FlexboxGrid.Item>

        <FlexboxGrid.Item colspan={6}>
          <Form.ControlLabel>Оплата</Form.ControlLabel>
          <Form.Control accepter={RadioGroup} name="payment_type">
            {Object.entries(PAYMENT_METHODS).map(([ key, paymentMethod ]) => (
              <Radio key={key} value={key}>
                {paymentMethod.description}
              </Radio>
            ))}
          </Form.Control>
        </FlexboxGrid.Item>
      </FlexboxGrid>

      {/*  */}

      <Divider />

      {shippingType.needAddress && (
        <div className="my-2">
          <h3 className="u-form-heading">Адрес</h3>
          <div className="my-2">
            <CustomerAddress
              className="StepAddress__CustomerAddress"
              onAddressChosen={handleAddressChosen}
              placeholder="Укажите адрес доставки"
              name="address"
            />

            {/*  */}
            {formState.customerGeoPoint && (
              <Grid fluid>
                <Row className="OrderFormAddress__Row">
                  <Col xs={12} sm={6}>
                    <Form.Control
                      name="address_appartments"
                      placeholder="Квартира/офис"
                    />
                  </Col>
                  <Col xs={12} sm={6}>
                    <Form.Control
                      name="address_intercom"
                      placeholder="Код домофона"
                    />
                  </Col>
                  <Col xs={12} sm={6}>
                    <Form.Control name="address_porch" placeholder="Подъезд" />
                  </Col>
                  <Col xs={12} sm={6}>
                    <Form.Control
                      name="address_floor"
                      placeholder="Этаж"
                      accepter={InputNumber}
                      min={-3}
                      max={40}
                    />
                  </Col>
                </Row>

                <Row className="OrderFormAddress__Row">
                  <Col xs={18}>
                    <FormTextareaControl
                      name="comment"
                      placeholder="Комментарий к заказу"
                      className="StepDelivert__Comment"
                      rows={4}
                    />
                  </Col>
                  <Col xs={6}>
                    <FormCheckbox.Control
                      name="door_delivery"
                      className="CustomerInfo__comment"
                    >
                      Доставка до двери
                    </FormCheckbox.Control>
                  </Col>
                </Row>

                <Row gutter={2}>
                  <Col xs={24}>
                    {shippingType.isSlotBased && (
                      <SlotsTable
                        slotId={formState.slot_id}
                        shop_id={order.shop_id}
                        onSlotSelect={handleSlotSelect}
                      />
                    )}
                  </Col>
                </Row>
              </Grid>
            )}
          </div>
        </div>
      )}

      <div className="my-4">
        <h3 className="u-form-heading">Товары</h3>
        {/* product cart here */}
        {!cartIsValid && (
          <FormElementErrorDisplay
            field="cart"
            error={{
              cart: cartErrorLabel
            }}
          />
        )}
        {order.customerProducts && (
          <>
            <OrderTabbedCart onChange={setCartIsValid} order={order} />
            <OrderCost readOnly order={order} />
          </>
        )}
      </div>

      <div className="flex justify-center my-1">
        <ButtonGroup className="clearfix">
          <Button
            name="save"
            type="submit"
            className="u-btn-submit"
            color="blue"
            size="lg"
            disabled={!cartIsValid}
            onClick={() => {
              submitType.current = FORM_SUBMIT_TYPES.SUBMIT_TO_SAVE
            }}
          >
            Сохранить
          </Button>
          <Button
            name="publish"
            type="submit"
            className="u-btn-submit"
            color="green"
            size="lg"
            disabled={!cartIsValid || !orderCanBePlaced}
            onClick={() => {
              submitType.current = FORM_SUBMIT_TYPES.SUBMIT_TO_PUBLISH
            }}
          >
            Разместить
          </Button>
        </ButtonGroup>
      </div>
    </Form>
  )
}
